import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { ConfirmDialogService } from '../../dialog/confirm-dialog/confirm-dialog.service';
import { BookingRule } from '../../model/booking-rule.model';
import { BookingDefinitionService } from './bookingdefinition.service';

/** Dependencies required to run the shared booking definition delete flow. */
export interface DeleteBookingDefinitionOptions {
  /** Id of the booking definition to delete. */
  id: string;
  service: BookingDefinitionService;
  dialogService: ConfirmDialogService;
  destroyRef: DestroyRef;
  /** Called after the booking definition has been removed successfully. */
  onDeleted: () => void;
}

/**
 * Runs the shared "Verwijderen" flow for a booking definition:
 * checks for booking rules that still use it and either shows a blocking
 * dialog or asks for confirmation before deleting.
 */
export function deleteBookingDefinitionWithChecks(
  options: DeleteBookingDefinitionOptions,
): void {
  const { id, service, dialogService, destroyRef } = options;

  // A booking definition cannot be removed while booking rules still use it.
  service
    .getByBookingDefinitionId(id)
    .pipe(takeUntilDestroyed(destroyRef))
    .subscribe({
      next: (rules) => {
        if (rules && rules.length > 0) {
          showDeleteNotPossibleDialog(dialogService, rules);
        } else {
          confirmAndDeleteBookingDefinition(options);
        }
      },
      error: () => confirmAndDeleteBookingDefinition(options),
    });
}

function showDeleteNotPossibleDialog(
  dialogService: ConfirmDialogService,
  rules: BookingRule[],
): void {
  dialogService.open({
    width: '400px',
    title: 'Verwijderen niet mogelijk',
    message: `Er zijn ${rules.length} boekingsregels die deze boekingsdefinitie gebruiken. Deze moeten eerst verwijderd worden voordat dit record verwijderd kan worden.`,
    confirmText: 'OK',
    variant: 'warning',
  });
}

function confirmAndDeleteBookingDefinition(options: DeleteBookingDefinitionOptions): void {
  const { id, service, dialogService, destroyRef, onDeleted } = options;

  dialogService.open({
    width: '320px',
    title: 'Verwijderen?',
    message: 'Boekingsdefinitie verwijderen?',
    cancelText: 'Nee',
    confirmText: 'Ja',
    variant: 'error',
  });

  dialogService
    .confirmed()
    .pipe(
      take(1),
      switchMap((confirmed) => {
        if (!confirmed) {
          return EMPTY;
        }

        return service.removeBookingDefinition(id);
      }),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe({
      next: () => onDeleted(),
      error: () => undefined,
    });
}
